import React, { useContext, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  Flex,
  Heading,
  Image,
  Input,
  Link,
  Stack,
  Text,
} from "@chakra-ui/react";
import { PRIMARY_COLOR } from "../../constants/colors";
import api from "../../services/api";
import { toast } from "react-toastify";
import { AuthContext } from "../../contexts/AuthContext";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { maskCpfCnpj, onlyDigits } from "../../utils/cpfCnpj";
import {
  resolveCatalogContext,
  buildCatalogPath,
  buildRegisterPath,
  buildForgotPasswordPath,
} from "../../utils/catalogContext";

export default function Login() {
  const [login, setLogin] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  const from = location.state?.from;

  // Fonte de verdade de qual catálogo o usuário está tentando acessar:
  // vem da própria URL (/login/:clientHash/:sellerId) quando presente,
  // com fallback pro que ficou salvo na última visita ao catálogo.
  const { clientHash, sellerId } = resolveCatalogContext(params);

  const { setUser } = useContext(AuthContext);

  const handleLoginChange = (e) => {
    const value = e.target.value;
    // Se o usuário está digitando apenas números, aplica a máscara de CPF/CNPJ
    setLogin(/^[\d.\-/]*$/.test(value) ? maskCpfCnpj(onlyDigits(value)) : value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await api.post("/login/clients", { login, password });
      const token = response.data.token;
      setUser({ token, info: response.data.client });
      localStorage.setItem("@aqvex/user", JSON.stringify(response.data.client));
      localStorage.setItem("@aqvex/token", JSON.stringify(token));

      toast.success("Login realizado com sucesso!");

      // Nunca usa histórico do navegador (navigate(-1)) pra decidir o destino
      // — sem uma página anterior confiável isso deixa o usuário preso no
      // /login. O destino é sempre calculado: a rota explícita que trouxe o
      // usuário até aqui (from) ou o catálogo que originou este login.
      navigate(from || buildCatalogPath(clientHash, sellerId), { replace: true });
    } catch (err) {
      toast.error(
        err.response.data.error ??
          "Erro ao realizar login, tente novamente mais tarde!",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Flex
      minH="100vh"
      align="center"
      justify="center"
      bg="gray.50"
      p={4}
      flexDirection="column"
      gap="40px"
    >
      <Image src="/logo-aqvex.png" cursor="pointer" width="200px" />
      <Box
        as="form"
        onSubmit={handleSubmit}
        bg="white"
        rounded="lg"
        boxShadow="lg"
        p={{ base: 6, md: 10 }}
        width={{ base: "100%", sm: "420px" }}
      >
        <Flex flexDirection="column" width="100%" gap="10px">
          {" "}
          <Flex flexDirection="column" gap="10px">
            <Text fontWeight="600">Email ou CPF/CNPJ *</Text>
            <Input
              type="text"
              placeholder="Digite seu email ou CPF/CNPJ..."
              padding="15px"
              value={login}
              onChange={handleLoginChange}
              required
            />
          </Flex>
          <Flex flexDirection="column" gap="10px">
            <Text fontWeight="600">Senha de Acesso do Aplicativo *</Text>
            <Input
              type="password"
              placeholder="Digite sua senha..."
              padding="15px"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <Text textAlign="right" fontSize="sm">
              <Link
                color={PRIMARY_COLOR}
                fontWeight="600"
                cursor="pointer"
                onClick={() => navigate(buildForgotPasswordPath(clientHash, sellerId))}
              >
                Esqueci minha senha
              </Link>
            </Text>
          </Flex>
          <Button
            type="submit"
            colorScheme="blue"
            isFullWidth
            loading={isSubmitting}
            width="100%"
            backgroundColor={PRIMARY_COLOR}
          >
            Entrar
          </Button>
          <Text textAlign="center" fontSize="sm" mt={2}>
            Não tem cadastro?{" "}
            <Link
              color={PRIMARY_COLOR}
              fontWeight="600"
              cursor="pointer"
              onClick={() =>
                navigate(buildRegisterPath(clientHash, sellerId), { state: { from } })
              }
            >
              Cadastre-se
            </Link>
          </Text>
        </Flex>
      </Box>
    </Flex>
  );
}
